import type { CategoriesParams, CategoriesQueryInput } from '../-types';

/** The five fields the categories list maps into its `ORDER BY`. */
const SORTABLE_FIELDS = new Set([
  'name',
  'hierarchyLevel',
  'createdAt',
  'updatedAt',
  'isWeightAvailable',
]);

/**
 * The columns a user may filter on.
 *
 * `parentId` is deliberately not here: it is the drill-down's scope and is set
 * below on every request. `hierarchyLevel` is not here either: it is
 * redundant, since `parentId` already picks out one level.
 */
const FILTERABLE_FIELDS = new Set(['name', 'quantityUnits', 'weightUnits']);

/**
 * Turns table state into the categories endpoint's parameters.
 *
 * `page` converts from the UI's 1-based count to the endpoint's 0-based one.
 * `filter` is a JSON object of `column: value`, capped at 512 characters by the
 * route.
 *
 * `parentId` is always sent, including as `null`, which the route's filter
 * builder turns into `parent_id IS NULL` — the root list. Because
 * `hierarchy_level` is derived from the parent on create, scoping by parent
 * alone selects exactly one level, so the level itself never has to be sent
 * and the list does not have to wait on the parent to be loaded first.
 */
export function toCategoriesParams(
  input: CategoriesQueryInput,
): CategoriesParams {
  const filter: Record<string, string | null> = { parentId: input.parentId };
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    if (FILTERABLE_FIELDS.has(key) && values && values.length > 0) {
      filter[key] = values.join(',');
    }
  }

  // Dropped rather than forwarded: an unsupported field would 400 the list.
  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(sort ? { sort } : {}),
    filter: JSON.stringify(filter),
  };
}

export function buildCategoriesQs(params: CategoriesParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}

/**
 * How many fields one request asks for.
 *
 * The route's ceiling (`config.pagination.maxCount`). The sheet needs every
 * field to work out the next `idx`, and the table is unpaginated, so a short
 * page would silently hide fields and hand out an `idx` that already exists.
 * A category with more fields than this is reported rather than truncated
 * quietly — see `useAdminCategoryFields`.
 */
export const CATEGORY_FIELDS_PAGE_SIZE = 500;

/**
 * The querystring for one category's fields.
 *
 * The fields route has no `sort` and selects its category through `filter`
 * rather than a path segment, so this is the only way to scope the list.
 */
export function buildCategoryFieldsQs(categoryId: string): string {
  const qs = new URLSearchParams();
  qs.set('page', '0');
  qs.set('count', String(CATEGORY_FIELDS_PAGE_SIZE));
  qs.set('filter', JSON.stringify({ productCategoryId: categoryId }));
  return qs.toString();
}
